import { TFunction } from "i18next";
import * as Yup from "yup";

import { CounselorDutyType } from "@/api";

import { BlockedPeriodRowValues, CounselorDutyPreferencesFormValues } from "./CounselorDutyPreferences.types";

export const DUTY_TYPE_LABEL_KEYS = {
  [CounselorDutyType.Aw12]: "counselors.dutyPreferences.dutyTypes.aw12",
  [CounselorDutyType.Aw3]: "counselors.dutyPreferences.dutyTypes.aw3",
  [CounselorDutyType.Court]: "counselors.dutyPreferences.dutyTypes.bg",
} as const satisfies Record<CounselorDutyType, string>;

export function counselorDutyPreferencesValidationSchema(t: TFunction) {
  return Yup.object().shape({
    courtHours: Yup.number().typeError(t("common.validation.invalidNumber")).min(0).nullable(),
    awHours: Yup.number().typeError(t("common.validation.invalidNumber")).min(0).nullable(),
    excludedCourtIds: Yup.array()
      .of(Yup.string().required())
      .test("no-overlap", t("counselors.dutyPreferences.courts.courtInBothLists"), (excluded, ctx) => {
        const preferred = (ctx.parent as CounselorDutyPreferencesFormValues).preferredCourtIds;
        return !(excluded ?? []).some((id) => preferred.includes(id));
      }),
    blockedPeriods: Yup.array().of(
      Yup.object().shape({
        dutyType: Yup.string()
          .nullable()
          .required(
            t("common.validation.fieldRequired", { fieldName: t("counselors.dutyPreferences.blockedPeriods.dutyType") })
          ),
        from: Yup.date()
          .typeError(t("common.validation.invalidDateFormat"))
          .nullable()
          .required(
            t("common.validation.fieldRequired", { fieldName: t("counselors.dutyPreferences.blockedPeriods.from") })
          ),
        to: Yup.date()
          .typeError(t("common.validation.invalidDateFormat"))
          .nullable()
          .required(
            t("common.validation.fieldRequired", { fieldName: t("counselors.dutyPreferences.blockedPeriods.to") })
          )
          .min(Yup.ref("from"), t("counselors.dutyPreferences.validation.invalidDateRange"))
          .test(
            "no-overlapping-period-per-duty-type",
            t("counselors.dutyPreferences.validation.overlappingBlockedPeriod"),
            (_to, ctx) => {
              const row = ctx.parent as BlockedPeriodRowValues;
              const root = ctx.from?.[ctx.from.length - 1]?.value as CounselorDutyPreferencesFormValues | undefined;
              return !overlapsAnotherPeriodOfSameDutyType(row, root?.blockedPeriods ?? []);
            }
          ),
      })
    ),
  });
}

function overlapsAnotherPeriodOfSameDutyType(row: BlockedPeriodRowValues, periods: BlockedPeriodRowValues[]): boolean {
  const { dutyType, from, to } = row;
  if (dutyType == null || from == null || to == null) {
    return false;
  }

  return periods.some(
    (other) =>
      other !== row &&
      other.dutyType === dutyType &&
      other.from != null &&
      other.to != null &&
      from <= other.to &&
      other.from <= to
  );
}
